'use client'

import DestinationSuggestionList from '@/src/components/common/home/hotelSearch/destinationSuggestionList'
import { Box, Button, CircularProgress, ClickAwayListener, InputBase, Paper, Popper, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React, { useRef, useState } from 'react'

import { GuestRoomSelector } from '../guestRoomSelector'
import { HotelDateRangeField } from '../hotelDateRangeField'
import { useHotelSearchManager } from './hooks/useHotelSearchManager'

interface DesktopHotelSearchFormProps {
    searchManager: ReturnType<typeof useHotelSearchManager>
    showError: (message: string) => void
    onSearched?: () => void
}

type ActiveComponent = 'destination' | 'dates' | 'guests' | null

const BORDER_COLOR = 'grey.300'

const getCellSx = ({
    flex = 1,
    hasDivider = true,
    cursor = 'pointer'
}: {
    flex?: number
    hasDivider?: boolean
    cursor?: string
}) => ({
    display: 'flex',
    alignItems: 'center',
    minHeight: 70,
    flex,
    p: 2,
    minWidth: 0,
    cursor,
    ...(hasDivider && {
        borderLeft: '1px solid',
        borderColor: 'borders.default'
    })
})

const DesktopHotelSearchForm: React.FC<DesktopHotelSearchFormProps> = ({ searchManager, showError, onSearched }) => {
    const locale = useLocale()
    const t = useTranslations('common')
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    const {
        destination,
        handleDestinationInputChange,
        handleDestinationSelect,
        destinationSuggestions,
        destinationLoading,
        checkIn,
        checkOut,
        nights,
        roomConfigs,
        handleCheckInChange,
        handleCheckOutChange,
        handleRoomCountChange,
        handleRoomAdultsChange,
        handleRoomChildrenChange,
        minRooms,
        maxRooms,
        minAdultsPerRoom,
        maxAdultsPerRoom,
        minChildrenPerRoom,
        maxChildrenPerRoom,
        isSearchReady,
        minCheckOutDate,
        performHotelSearch
    } = searchManager

    const destinationWrapperRef = useRef<HTMLDivElement | null>(null)

    const isComponentActive = (component: ActiveComponent) => activeComponent === component

    const handleComponentActivation = (component: ActiveComponent) => {
        if (activeComponent === component && component !== 'destination') {
            setActiveComponent(null)
        } else {
            setActiveComponent(component)
        }
    }

    const closeActiveComponent = () => setActiveComponent(null)

    // The calendar and guest popovers render in a portal, so MUI reports clicks inside
    // them as "away" from the form and would collapse the active field — including the
    // hand-off from check-in to check-out. Treat those as inside.
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement | null
        if (target?.closest?.('.MuiPopover-root, .MuiMenu-root, .MuiPopper-root')) return
        if (activeComponent) {
            setActiveComponent(null)
        }
    }

    const handleSearch = () => {
        if (!isSearchReady) {
            showError(t('pleaseFillAllRequiredFields'))
            return
        }
        setActiveComponent(null)
        if (performHotelSearch(locale)) onSearched?.()
    }

    return (
        <Box sx={{ position: 'relative', zIndex: 0 }} data-home-hotel-search-form>
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    data-search-container
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        border: 1,
                        borderColor: BORDER_COLOR,
                        borderRadius: '0.75rem',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <Box sx={getCellSx({ flex: 1.56, hasDivider: false, cursor: 'text' })}>
                        <Box ref={destinationWrapperRef} sx={{ width: '100%' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('destination')}
                            </Typography>
                            <InputBase
                                value={destination}
                                onChange={e => {
                                    handleDestinationInputChange(e.target.value)
                                    setActiveComponent('destination')
                                }}
                                placeholder={t('hotelDestinationPlaceholder')}
                                onFocus={() => handleComponentActivation('destination')}
                                onClick={() => handleComponentActivation('destination')}
                                inputProps={{ style: { fontSize: 14 } }}
                                sx={{
                                    width: '100%',
                                    '& input': {
                                        padding: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />

                            <Popper
                                open={
                                    activeComponent === 'destination' &&
                                    (!!destinationLoading || !!destinationSuggestions.length)
                                }
                                anchorEl={destinationWrapperRef.current}
                                placement="bottom-start"
                                disablePortal={false}
                                style={{
                                    zIndex: 2000,
                                    width: destinationWrapperRef.current
                                        ? destinationWrapperRef.current.offsetWidth
                                        : undefined
                                }}
                            >
                                <Paper
                                    sx={{ minWidth: 450, maxHeight: 450, overflowY: 'auto', width: '100%', mt: 1.5 }}
                                    elevation={3}
                                >
                                    {destinationLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : destinationSuggestions.length ? (
                                        <DestinationSuggestionList
                                            suggestions={destinationSuggestions}
                                            onSelect={suggestion => {
                                                handleDestinationSelect(suggestion)
                                                setActiveComponent(null)
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('noDestinationFound')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Popper>
                        </Box>
                    </Box>

                    {/* One cell for both stay dates — they share a single calendar popover. */}
                    <Box sx={{ ...getCellSx({ flex: 2 }), p: 0 }}>
                        <HotelDateRangeField
                            checkIn={checkIn}
                            checkOut={checkOut}
                            minCheckOutDate={minCheckOutDate}
                            active={isComponentActive('dates')}
                            onActivate={() => setActiveComponent('dates')}
                            onClose={closeActiveComponent}
                            onCheckInChange={handleCheckInChange}
                            onCheckOutChange={handleCheckOutChange}
                        />
                    </Box>

                    <Box sx={getCellSx({ flex: 0.6, cursor: 'default' })}>
                        <Box sx={{ textAlign: 'center', width: '100%' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('nights')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {nights ?? '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={getCellSx({ flex: 1 })} onClick={() => handleComponentActivation('guests')}>
                        <GuestRoomSelector
                            roomConfigs={roomConfigs}
                            minRooms={minRooms}
                            maxRooms={maxRooms}
                            minAdultsPerRoom={minAdultsPerRoom}
                            maxAdultsPerRoom={maxAdultsPerRoom}
                            minChildrenPerRoom={minChildrenPerRoom}
                            maxChildrenPerRoom={maxChildrenPerRoom}
                            onRoomCountChange={handleRoomCountChange}
                            onRoomAdultsChange={handleRoomAdultsChange}
                            onRoomChildrenChange={handleRoomChildrenChange}
                            active={isComponentActive('guests')}
                            onActivate={() => handleComponentActivation('guests')}
                            onClose={closeActiveComponent}
                        />
                    </Box>

                    <Box sx={getCellSx({ flex: 0.9, cursor: 'default' })}>
                        <Button
                            variant="customVariantPrimary"
                            sx={{
                                borderRadius: '0.75rem',
                                py: 1.5,
                                px: 2,
                                minWidth: 100,
                                width: '100%',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={handleSearch}
                        >
                            {t('searchHotels')}
                        </Button>
                    </Box>
                </Box>
            </ClickAwayListener>
        </Box>
    )
}

export default DesktopHotelSearchForm
